import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Play, Handshake } from "lucide-react";

export const HeroSection = () => {
  const scrollTo = (id) =>
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-background" />
      <div
        className="absolute inset-0 opacity-5 dark:opacity-15"
        style={{
          backgroundImage: "url(https://images.unsplash.com/photo-1771773490670-7376a45c0e96?q=80&w=2000&auto=format&fit=crop)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-background via-background/80 to-background" />
      <div className="absolute inset-0 hero-grid opacity-40" />

      {/* Light mode warm gradient */}
      <div className="absolute inset-0 light-hero-gradient dark:hidden" />

      {/* Floating orbs */}
      <div className="absolute top-20 right-20 w-72 h-72 rounded-full bg-[#c9a84c]/[0.07] dark:bg-[#c9a84c]/5 blur-3xl animate-float" />
      <div className="absolute bottom-20 left-20 w-96 h-96 rounded-full bg-[#c9a84c]/[0.04] dark:bg-[#c9a84c]/[0.03] blur-3xl animate-float-delayed" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center pt-36 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Badge
            variant="outline"
            className="border-[#c9a84c]/40 text-[#c9a84c] bg-[#c9a84c]/10 px-4 py-1.5 text-sm font-body mb-8 inline-flex"
            data-testid="hero-badge"
          >
            India-Focused Privacy-First Enterprise AI
          </Badge>
        </motion.div>

        <motion.h1
          className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-foreground tracking-tight leading-tight max-w-5xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          data-testid="hero-headline"
        >
          Building Sovereign AI for a{" "}
          <span className="text-gradient-gold">Secure and Intelligent</span>{" "}
          Future.
        </motion.h1>

        <motion.p
          className="font-body text-muted-foreground text-base md:text-lg max-w-2xl mx-auto mt-6 leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          data-testid="hero-subheadline"
        >
          Ojas AI develops private, enterprise-grade AI systems that empower
          industries while preserving data sovereignty, regulatory compliance,
          and trust.
        </motion.p>

        <motion.div
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45 }}
        >
          <Button
            onClick={() => scrollTo("#solutions")}
            className="bg-[#c9a84c] text-[#0a1628] font-heading font-semibold px-8 py-6 text-base hover:bg-[#c9a84c]/90 group"
            data-testid="hero-explore-btn"
          >
            Explore Our Platforms
            <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
          </Button>
          <Button
            onClick={() => scrollTo("#contact")}
            variant="outline"
            className="border-[#c9a84c]/40 text-[#c9a84c] font-heading font-semibold px-8 py-6 text-base hover:bg-[#c9a84c]/10"
            data-testid="hero-demo-btn"
          >
            <Play className="mr-2 h-4 w-4" />
            Request Demo
          </Button>
          <Button
            onClick={() => scrollTo("#contact")}
            variant="outline"
            className="border-border text-muted-foreground font-heading px-8 py-6 text-base hover:bg-muted hover:text-foreground"
            data-testid="hero-partner-btn"
          >
            <Handshake className="mr-2 h-4 w-4" />
            Partner With Us
          </Button>
        </motion.div>

        <motion.p
          className="font-body text-muted-foreground/60 text-xs mt-16 tracking-widest uppercase"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          Built by domain experts with vivid experience across prestigious global organisations
        </motion.p>
      </div>
    </section>
  );
};
